/**
 * Picks the file a campaign sends in its template's media header.
 *
 * Quick-upload right here for the common case (pick a fresh file, done), or "Browse gallery" to
 * search/reuse/rename/delete across everything already uploaded — the gallery is opened locked to
 * this field's `kind`, so it only ever offers files the selected template can actually use.
 */
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Upload, X, FileText, Film, Image as ImageIcon, Loader2, LayoutGrid } from 'lucide-react';
import { toast } from 'react-toastify';
import { useListWaMediaQuery, waMediaContentUrl, uploadWaMediaWithProgress } from '../../api/whatsappMediaApi';
import MediaGalleryModal from './MediaGalleryModal';

const ACCEPT = {
  Image: 'image/jpeg,image/png',
  Video: 'video/mp4',
  Document: 'application/pdf',
};

const HINT = {
  Image: 'JPEG or PNG, up to 5 MB',
  Video: 'MP4, up to 16 MB',
  Document: 'PDF, up to 16 MB',
};

const KindIcon = ({ kind, size = 20 }) => {
  if (kind === 'Video') return <Film size={size} />;
  if (kind === 'Document') return <FileText size={size} />;
  return <ImageIcon size={size} />;
};

const fmtSize = (bytes) => {
  const mb = (bytes ?? 0) / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round((bytes ?? 0) / 1024)} KB`;
};

export default function MediaPickerField({ kind, value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const token = useSelector((s) => s.auth?.accessToken);

  // Only needed to render the currently-selected card without opening the gallery — a single-item
  // lookup, not the full listing.
  const { data: library } = useListWaMediaQuery({ kind });
  const selected = (library?.items ?? []).find((m) => m.id === value) ?? null;

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploading(true);
    setProgress(0);
    try {
      const created = await uploadWaMediaWithProgress(file, token, setProgress);
      if (created.kind !== kind) {
        toast.error(`That's a ${created.kind.toLowerCase()} file, but this template needs a ${kind.toLowerCase()} header.`);
        return;
      }
      onChange(created.id);
      toast.success(`${created.fileName} uploaded.`);
    } catch (err) {
      toast.error(err?.message || 'Could not upload that file.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="form-label">
        {kind === 'Document' ? 'PDF' : kind} for this campaign <span className="req">*</span>
      </label>

      {selected ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: 8,
          border: '1px solid var(--border)', borderRadius: 'var(--r-md, 6px)',
        }}>
          {selected.kind === 'Image' ? (
            <img
              src={waMediaContentUrl(selected.id)}
              alt=""
              style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
            />
          ) : (
            <span style={{
              width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--bg-hover)', borderRadius: 4, flexShrink: 0,
            }}>
              <KindIcon kind={selected.kind} size={20} />
            </span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.84rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selected.displayName}
            </div>
            <div className="td-muted" style={{ fontSize: '0.74rem' }}>{fmtSize(selected.sizeBytes)}</div>
          </div>
          <button type="button" className="btn-icon-sm" title="Remove" onClick={() => onChange(null)}>
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="td-muted" style={{ fontSize: '0.78rem', marginBottom: 6 }}>
          No file chosen yet — this campaign can't be switched on until one is.
        </div>
      )}

      {uploading && (
        <div style={{ height: 4, borderRadius: 999, background: 'var(--gray-100)', overflow: 'hidden', margin: '8px 0' }}>
          <div style={{ width: `${progress}%`, height: '100%', background: 'var(--blue-500)', transition: 'width 0.15s' }} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT[kind] ?? ''}
          style={{ display: 'none' }}
          onChange={pick}
        />
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <Loader2 size={14} className="spin" /> : <Upload size={14} />}
          {uploading ? `Uploading… ${progress}%` : 'Upload new'}
        </button>

        <button type="button" className="btn btn-outline btn-sm" onClick={() => setShowGallery(true)} disabled={uploading}>
          <LayoutGrid size={14} /> Browse gallery
        </button>
      </div>

      <div className="td-muted" style={{ fontSize: '0.74rem', marginTop: 4 }}>
        {HINT[kind]}. The same template can send a different file for every campaign.
      </div>

      {showGallery && (
        <MediaGalleryModal
          lockKind={kind}
          onSelect={(id) => { onChange(id); setShowGallery(false); }}
          onClose={() => setShowGallery(false)}
        />
      )}
    </div>
  );
}
